import { sortBy } from 'lodash';
import type { IntlShape } from 'react-intl';

import { FORM_INFO_TAB_INDEX } from '~/common/constants/ticket';
import { Box } from '~/eds/src/components/Box/Box';
import { Link } from '~/eds/src/components/Link/Link';
import { Text } from '~/eds/src/components/Text/Text';
import { User } from '~/eds/src/components/User/User';
import { User as EdsUser } from '~/eds/src/types/User';
import { getFileBasename } from '~/eds/src/utils/getFileBasename';
import { getFileExtension } from '~/eds/src/utils/getFileExtension';
import { getUserName } from '~/eds/src/utils/getUserName';
import { EsignatureType } from '~/enums/EsignatureType';
import { InboundAttachmentException } from '~/enums/InboundAttachmentException';
import { TicketActivityType } from '~/enums/TicketActivityType';
import { TicketStageType } from '~/enums/TicketStageType';
import { TicketStatusType } from '~/enums/TicketStatusType';
import type { useFormatDateReturnType } from '~/i18n/hooks/useFormatDate/types';
import { RoutePathType } from '~/routing/enums';
import { ActivityLogEvent, ActivityLogUser } from '~/types/ActivityLog';
import { Nullable } from '~/types/Nullable';
import { PilotId } from '~/types/PilotId';
import { TicketDocumentVersion } from '~/types/Ticket';
import { Uuid } from '~/types/Uuid';
import { joinWith } from '~/utils/array';

import { AttachmentData, CustomActions } from './types';

function getPlaceholder(data: Array<string | number>) {
  return `{{${data.join(':')}}}`;
}

function getEventActor(event: ActivityLogEvent) {
  if (!event.user) {
    return getPlaceholder(['system', '0']);
  }
  return getPlaceholder(['user', event.user.id]);
}

const joinStrings = (array: string[]) =>
  joinWith(array, { delimiter: ', ', lastDelimiter: ' and ' });

function getSharedVersionsMessage(
  attachments: AttachmentData[],
  ticketId: Uuid,
) {
  const existingVersionsSorted = sortBy(
    attachments.filter((attachment) => attachment.type === 'existingVersion'),
    (attachment) => attachment.tag,
  ).map(
    (version) => `${getPlaceholder(['versionLink', version.tag!, ticketId])}`,
  );

  return joinStrings(existingVersionsSorted);
}

function getShareRecipientsMessage(emails: string[]) {
  return joinStrings(
    emails.map((email) => `${getPlaceholder(['bold', email])}`),
  );
}

function formatEventPhaseAndTask(phase: string, task: string) {
  return getPlaceholder(['bold', `${phase} -- ${task}`]);
}

export const tokenRenderer = (
  users: Record<PilotId, EdsUser>,
  customActions: CustomActions = {},
  versions: TicketDocumentVersion[],
  intl: IntlShape,
) => ({
  bold: (value: string) => <Text variant="body-bold">{value}</Text>,
  system: (_value: string) => (
    <Text variant="body-bold">
      {intl.formatMessage({
        id: 'TokenRenderer.systemUser',
        defaultMessage: 'System',
        description: 'Text shown for system-initiated actions in activity log',
      })}
    </Text>
  ),
  versionLink: (value: string) => {
    const [versionNumber, ticketId] = value.split(':');
    const isValidVersion = versions.some(
      (documentVersion) =>
        documentVersion.versionNumber === Number(versionNumber),
    );
    if (isValidVersion) {
      return (
        <Link
          variant="body"
          pathname={RoutePathType.WorkflowTicketsTicketReviewerVersionNumber.replace(
            ':ticketId',
            ticketId,
          ).replace(':versionNumber', versionNumber)}
        >
          {intl.formatMessage(
            {
              id: 'ActivityLogUtils.versionTextLink',
              defaultMessage: 'version {versionNumber}',
              description: 'Text for version number link',
            },
            { versionNumber },
          )}
        </Link>
      );
    } else {
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.noLinkversionText',
          defaultMessage: 'version {versionNumber}',
          description: 'Text for non-link version number',
        },
        { versionNumber },
      );
    }
  },
  intakeFormLink: (value: string) => {
    const [ticketId] = value.split(':');
    const formInformationTextContent = intl.formatMessage({
      id: 'ActivityLogUtils.formInformation',
      defaultMessage: 'form information',
      description: 'Links to the intake form information',
    });

    return customActions.intakeFormAction ? (
      <Link variant="body" onClick={customActions.intakeFormAction}>
        {formInformationTextContent}
      </Link>
    ) : (
      <Link
        variant="body"
        pathname={RoutePathType.WorkflowTicketsTicket.replace(
          ':ticketId',
          ticketId,
        )}
        search={`activeTab=${FORM_INFO_TAB_INDEX}`}
      >
        {formInformationTextContent}
      </Link>
    );
  },
  user: (value: string) => {
    const [userId] = value.split(':');
    const user = users[parseInt(userId)];
    return user ? (
      <Box display="inline-block">
        <User
          mode="name"
          user={{
            firstName: user.firstName,
            id: user.id,
            lastName: user.lastName,
            email: null,
          }}
          options={{ isDeleted: user.isDeleted, shouldTruncate: false }}
        />
      </Box>
    ) : (
      <Text variant="body-bold">
        {intl.formatMessage({
          id: 'TokenRenderer.deactivatedUser',
          defaultMessage: 'Deactivated User',
          description: 'Text shown for deactivated user in activity log',
        })}
      </Text>
    );
  },
  breakLine: () => {
    return <br />;
  },
  indent: () => {
    return <>&nbsp;&nbsp;&nbsp;&nbsp;</>;
  },
});

export const tokenSearchRenderer = (
  users: Record<PilotId, EdsUser>,
  intl: IntlShape,
) => ({
  bold: (value: string) => value,
  system: (_value: string) =>
    intl.formatMessage({
      id: 'TokenSearchRenderer.systemUser',
      defaultMessage: 'System',
      description:
        'Text shown for system-initiated actions in activity log search',
    }),
  versionLink: (value: string) => {
    const [versionNumber, _ticketId] = value.split(':');
    return `version ${versionNumber}`;
  },
  user: (value: string) => {
    const [userId] = value.split(':');
    const user = users[parseInt(userId)];
    return user
      ? getUserName({
          user,
          intl,
        })
      : intl.formatMessage({
          id: 'TokenSearchRenderer.deactivatedUser',
          defaultMessage: 'Deactivated User',
          description: 'Placeholder text for deactivated user in activity log',
        });
  },
});

export function getQuote(event: ActivityLogEvent): Nullable<string> {
  switch (event.action) {
    case TicketActivityType.JudgmentResultStatusUpdate:
    case TicketActivityType.DocumentUpload:
      return event.data.comment;
    case TicketActivityType.CancelTicket:
      return event.data.reason;
    default:
      return null;
  }
}

export function getActivityLogContent(
  event: ActivityLogEvent,
  versions: TicketDocumentVersion[],
  ticketId: Uuid,
  intl: IntlShape,
  formatDate: useFormatDateReturnType,
) {
  switch (event.action) {
    case TicketActivityType.AddParticipant:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.addParticipant',
          defaultMessage: '{actor} added {user} to the ticket.',
          description:
            'Activity log message for adding a participant to a ticket.',
        },
        {
          actor: getEventActor(event),
          user: getPlaceholder(['user', event.data.user.id]),
        },
      );
    case TicketActivityType.CancelEnvelope: {
      const { esignatureProvider } = event.data;
      return esignatureProvider
        ? intl.formatMessage(
            {
              id: 'ActivityLogUtils.cancelEnvelopeWithProvider',
              defaultMessage: '{actor} canceled the {provider} signatures.',
              description:
                'Activity log message for canceling envelope with specific e-signature provider',
            },
            {
              actor: getEventActor(event),
              provider: getEsigProviderText(esignatureProvider),
            },
          )
        : intl.formatMessage(
            {
              id: 'ActivityLogUtils.cancelEnvelope',
              defaultMessage: '{actor} canceled the signatures.',
              description: 'Activity log message for canceling envelope',
            },
            {
              actor: getEventActor(event),
            },
          );
    }
    case TicketActivityType.CancelTicket:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.cancelTicket',
          defaultMessage: '{actor} canceled this ticket.',
          description: 'Activity log message for canceling a ticket',
        },
        {
          actor: getEventActor(event),
        },
      );
    case TicketActivityType.CollectSignatures:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.collectSignatures',
          defaultMessage:
            '{actor} is sending this document to collect signatures.',
          description: 'Activity log message for collecting signatures',
        },
        {
          actor: getEventActor(event),
        },
      );
    case TicketActivityType.CollectSignaturesCancelled:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.collectSignaturesCancelled',
          defaultMessage: '{actor} canceled signature collection.',
          description:
            'Activity log message for canceling signature collection',
        },
        {
          actor: getEventActor(event),
        },
      );
    case TicketActivityType.CoordinatorReassignment:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.coordinatorReassignment',
          defaultMessage: '{actor} reassigned {stage} Coordinator to {user}.',
          description:
            'Activity log message for reassigning coordinator. Example: stage = "Review", "Sign"',
        },
        {
          actor: getEventActor(event),
          stage: getStageText(event.data.stage.toLowerCase(), intl),
          user: getPlaceholder(['user', event.data.newUser.id]),
        },
      );
    case TicketActivityType.CreateEnvelope: {
      const { esignatureProvider } = event.data;
      return esignatureProvider
        ? intl.formatMessage(
            {
              id: 'ActivityLogUtils.createEnvelopeWithProvider',
              defaultMessage:
                '{actor} is sending this ticket to collect signatures via {provider}.',
              description:
                'Activity log message for creating envelope with specific e-signature provider',
            },
            {
              actor: getEventActor(event),
              provider: getEsigProviderText(esignatureProvider),
            },
          )
        : intl.formatMessage(
            {
              id: 'ActivityLogUtils.createEnvelope',
              defaultMessage:
                '{actor} is sending this ticket to collect signatures.',
              description: 'Activity log message for creating envelope',
            },
            {
              actor: getEventActor(event),
            },
          );
    }
    case TicketActivityType.CreateTicket:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.createTicket',
          defaultMessage: '{actor} created this ticket.',
          description: 'Activity log message for creating a ticket',
        },
        {
          actor: getEventActor(event),
        },
      );
    case TicketActivityType.DeclineEnvelope:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.declineEnvelope',
          defaultMessage: '{actor} declined to sign the document.',
          description: 'Activity log message for declining to sign document',
        },
        {
          actor: getEventActor(event),
        },
      );
    case TicketActivityType.DeleteVersion:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.deleteVersion',
          defaultMessage: '{actor} deleted version {version}',
          description:
            'Activity log message for deleting a document version. Example: version = "1", "2"',
        },
        {
          actor: getEventActor(event),
          version: event.data.version.tag,
        },
      );
    case TicketActivityType.DocumentEdit:
      const {
        fileName: documentEditFileName,
        platform: editorType = 'Evisort',
        users: editors = [],
      } = event.data;
      const formattedFileName = getFileBasename(documentEditFileName);
      const editorPlaceholders = editors.map((editor: ActivityLogUser) =>
        getPlaceholder(['user', editor.id]),
      );
      const editorString = editors.length
        ? joinStrings(editorPlaceholders)
        : getEventActor(event);
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.documentEdit',
          defaultMessage: '{editors} edited {fileName} in {platform}.',
          description:
            'Activity log message for editing a document. Example: platform = "Evisort", "Word"',
        },
        {
          editors: editorString,
          fileName: formattedFileName,
          platform: editorType,
        },
      );
    case TicketActivityType.DocumentSave:
      const { comment, fileName } = event.data;
      if (comment === TicketActivityType.ConvertedDocument) {
        const formattedFileName = getFileBasename(fileName);
        const version = versions.find(
          (version) => version.id === event.data.version.id,
        );
        return intl.formatMessage(
          {
            id: 'ActivityLogUtils.documentConvert',
            defaultMessage:
              '{actor} converted {fileName} into a docx format which generated {versionLink}',
            description:
              'Activity log message for converting a document to docx format',
          },
          {
            actor: getEventActor(event),
            fileName: getPlaceholder(['bold', formattedFileName]),
            versionLink: getPlaceholder([
              'versionLink',
              version ? version.versionNumber : '',
              ticketId,
            ]),
          },
        );
      } else if (event.data.referenceVersionId !== event.data.targetVersionId) {
        return intl.formatMessage(
          {
            id: 'ActivityLogUtils.documentUploadWithRedlines',
            defaultMessage:
              '{actor} uploaded a new document with redlines, creating {versionLink}',
            description:
              'Activity log message for uploading document with redlines',
          },
          {
            actor: getEventActor(event),
            versionLink: getPlaceholder([
              'versionLink',
              event.data.version.tag,
              ticketId,
            ]),
          },
        );
      } else {
        return intl.formatMessage(
          {
            id: 'ActivityLogUtils.documentSave',
            defaultMessage: '{actor} saved document as {versionLink}',
            description: 'Activity log message for saving a document',
          },
          {
            actor: getEventActor(event),
            versionLink: getPlaceholder([
              'versionLink',
              event.data.version.tag,
              ticketId,
            ]),
          },
        );
      }
    case TicketActivityType.DocumentUpload:
      const fileFormat = getFileExtension(event.data.fileName).substring(1); //Remove the "." from the file extension
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.documentUpload',
          defaultMessage: '{actor} uploaded a {fileFormat} to {versionLink}',
          description:
            'Activity log message for uploading a document. Example: fileFormat = "PDF", "DOCX"',
        },
        {
          actor: getEventActor(event),
          fileFormat: fileFormat.toUpperCase(),
          versionLink: getPlaceholder([
            'versionLink',
            event.data.version.tag,
            ticketId,
          ]),
        },
      );
    case TicketActivityType.DownloadVersion:
    case TicketActivityType.DownloadDocument:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.downloadVersion',
          defaultMessage: '{actor} downloaded {versionLink}',
          description:
            'Activity log message for downloading a document version',
        },
        {
          actor: getEventActor(event),
          versionLink: getPlaceholder([
            'versionLink',
            event.data.version.tag,
            ticketId,
          ]),
        },
      );
    case TicketActivityType.Esignature:
      const { externalUser, internalUser } = event.data;
      const signatureUser = internalUser
        ? `${getPlaceholder(['user', internalUser.id])}`
        : `${getPlaceholder(['bold', externalUser.name])}`;
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.esignature',
          defaultMessage: '{user} signed the document via eSignature.',
          description:
            'Activity log message for signing document via eSignature',
        },
        {
          user: signatureUser,
        },
      );
    case TicketActivityType.InboundAttachmentAccepted: {
      const { counterpartySenderEmail, fileName, version, fileSize } =
        event.data;
      return fileSize
        ? intl.formatMessage(
            {
              id: 'ActivityLogUtils.inboundAttachmentAcceptedWithSizeLimit',
              defaultMessage:
                '{email} uploaded {fileName} which generated {versionLink} via email submission, and its file size exceeds limitation in e-signature.',
              description:
                'Activity log message for accepted inbound attachment with file size limitation',
            },
            {
              email: getPlaceholder(['bold', counterpartySenderEmail]),
              fileName: getPlaceholder(['bold', fileName]),
              versionLink: getPlaceholder([
                'versionLink',
                version.tag,
                ticketId,
              ]),
            },
          )
        : intl.formatMessage(
            {
              id: 'ActivityLogUtils.inboundAttachmentAccepted',
              defaultMessage:
                '{email} uploaded {fileName} which generated {versionLink} via email submission.',
              description:
                'Activity log message for accepted inbound attachment',
            },
            {
              email: getPlaceholder(['bold', counterpartySenderEmail]),
              fileName: getPlaceholder(['bold', fileName]),
              versionLink: getPlaceholder([
                'versionLink',
                version.tag,
                ticketId,
              ]),
            },
          );
    }
    case TicketActivityType.InboundAttachmentRejected: {
      const {
        counterpartySenderEmail,
        exception,
        stageAttempted,
        ticketName,
        totalAttachments,
      } = event.data;
      switch (exception) {
        case InboundAttachmentException.WrongStageException: {
          return intl.formatMessage(
            {
              id: 'ActivityLogUtils.inboundAttachmentRejectedWrongStage',
              defaultMessage:
                'Email submission sent in by {email} was rejected in {stage}.',
              description:
                'Activity log message for rejected inbound attachment due to wrong stage',
            },
            {
              email: getPlaceholder(['bold', counterpartySenderEmail]),
              stage: getStageText(stageAttempted.toLowerCase(), intl),
            },
          );
        }
        case InboundAttachmentException.TicketAlreadyCompletedException: {
          return intl.formatMessage(
            {
              id: 'ActivityLogUtils.inboundAttachmentRejectedCompleted',
              defaultMessage:
                'Email submission sent in by {email} was rejected because {ticketName} was completed.',
              description:
                'Activity log message for rejected inbound attachment due to completed ticket',
            },
            {
              email: getPlaceholder(['bold', counterpartySenderEmail]),
              ticketName,
            },
          );
        }
        case InboundAttachmentException.TicketCancelledException: {
          return intl.formatMessage(
            {
              id: 'ActivityLogUtils.inboundAttachmentRejectedCancelled',
              defaultMessage:
                'Email submission sent in by {email} was rejected because {ticketName} was canceled.',
              description:
                'Activity log message for rejected inbound attachment due to canceled ticket',
            },
            {
              email: getPlaceholder(['bold', counterpartySenderEmail]),
              ticketName,
            },
          );
        }
        case InboundAttachmentException.NoneOrMultipleAttachmentsException: {
          return intl.formatMessage(
            {
              id: 'ActivityLogUtils.inboundAttachmentRejectedAttachments',
              defaultMessage:
                'Email submission sent in by {email} was rejected for adding {totalAttachments, plural, zero {no attachment} one {one attachment} other {multiple attachments}}.',
              description:
                'Activity log message for rejected inbound attachment due to attachment issues',
            },
            {
              email: getPlaceholder(['bold', counterpartySenderEmail]),
              totalAttachments,
            },
          );
        }
        default: {
          return intl.formatMessage(
            {
              id: 'ActivityLogUtils.inboundAttachmentRejected',
              defaultMessage:
                'Email submission sent in by {email} was rejected.',
              description:
                'Activity log message for rejected inbound attachment (default case)',
            },
            {
              email: getPlaceholder(['bold', counterpartySenderEmail]),
            },
          );
        }
      }
    }
    case TicketActivityType.IntakeFormEdit:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.intakeFormEdit',
          defaultMessage: '{actor} edited the {formLink}.',
          description: 'Activity log message for editing intake form',
        },
        {
          actor: getEventActor(event),
          formLink: getPlaceholder(['intakeFormLink', ticketId]),
        },
      );
    case TicketActivityType.JudgmentResultAssignment:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.judgmentResultAssignment',
          defaultMessage: '{user} has been assigned approval of {phaseTask}.',
          description: 'Activity log message for assigning approval to user',
        },
        {
          user: getPlaceholder(['user', event.data.newUser.id]),
          phaseTask: formatEventPhaseAndTask(
            event.data.phase.name,
            event.data.name,
          ),
        },
      );
    case TicketActivityType.JudgmentReminderSent:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.judgmentReminderSent',
          defaultMessage:
            '{actor} sent a reminder for {phaseTask} to {recipient}',
          description: 'Activity log message for sending judgment reminder',
        },
        {
          actor: getEventActor(event),
          phaseTask: formatEventPhaseAndTask(event.data.phase, event.data.name),
          recipient: getPlaceholder(['bold', event.data.recipient_name]),
        },
      );
    case TicketActivityType.JudgmentResultReassignment:
      if (event.data.currentStage === TicketStageType.Sign) {
        return intl.formatMessage(
          {
            id: 'ActivityLogUtils.judgmentResultReassignmentSign',
            defaultMessage: '{actor} reassigned an approval to {user}.',
            description:
              'Activity log message for reassigning approval in sign stage',
          },
          {
            actor: getEventActor(event),
            user: getPlaceholder(['user', event.data.newUser.id]),
          },
        );
      }
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.judgmentResultReassignment',
          defaultMessage:
            '{actor} reassigned approval of {phaseTask} to {user}.',
          description:
            'Activity log message for reassigning approval with phase and task',
        },
        {
          actor: getEventActor(event),
          phaseTask: formatEventPhaseAndTask(
            event.data.phase.name,
            event.data.name,
          ),
          user: getPlaceholder(['user', event.data.newUser.id]),
        },
      );
    case TicketActivityType.JudgmentResultStatusUpdate:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.judgmentResultStatusUpdate',
          defaultMessage:
            '{actor} updated the status of {phaseTask} to {status}.',
          description:
            'Activity log message for updating judgment result status',
        },
        {
          actor: getEventActor(event),
          phaseTask: formatEventPhaseAndTask(
            event.data.phase.name,
            event.data.name,
          ),
          status: getPlaceholder([
            'bold',
            getStatusText(event.data.status, intl),
          ]),
        },
      );
    case TicketActivityType.NegotiationShare:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.negotiationShare',
          defaultMessage:
            '{actor} shared version {versionLink} with supplier contacts with the WSS Supplier Portal.',
          description:
            'Activity log message for sharing document with supplier portal',
        },
        {
          actor: getEventActor(event),
          versionLink: getPlaceholder([
            'versionLink',
            event.data.versionTag ?? '',
            ticketId,
          ]),
        },
      );
    case TicketActivityType.NegotiationRetract:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.negotiationRetract',
          defaultMessage:
            '{actor} removed version {versionLink} from WSS Supplier Portal.',
          description:
            'Activity log message for retracting document from supplier portal',
        },
        {
          actor: getEventActor(event),
          versionLink: getPlaceholder([
            'versionLink',
            event.data.versionTag ?? '',
            ticketId,
          ]),
        },
      );
    case TicketActivityType.NegotiationShareFailed:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.negotiationShareFailed',
          defaultMessage:
            '{actor} failed to share version {versionLink} with the WSS Supplier Portal.',
          description:
            'Activity log message when sharing with supplier portal fails',
        },
        {
          actor: getEventActor(event),
          versionLink: getPlaceholder([
            'versionLink',
            event.data.versionTag ?? '',
            ticketId,
          ]),
        },
      );
    case TicketActivityType.NegotiationRetractFailed:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.negotiationRetractFailed',
          defaultMessage:
            '{actor} failed to remove version {versionLink} from WSS Supplier Portal.',
          description:
            'Activity log message when retracting from supplier portal fails',
        },
        {
          actor: getEventActor(event),
          versionLink: getPlaceholder([
            'versionLink',
            event.data.versionTag ?? '',
            ticketId,
          ]),
        },
      );
    case TicketActivityType.NegotiationSupplierAccepted:
      return intl.formatMessage({
        id: 'ActivityLogUtils.negotiationSupplierAccepted',
        defaultMessage: 'Supplier accepted the terms.',
        description:
          'Activity log message when supplier accepts negotiation terms',
      });
    case TicketActivityType.NegotiationSupplierError:
      return intl.formatMessage({
        id: 'ActivityLogUtils.negotiationSupplierError',
        defaultMessage: 'An error occurred from the supplier.',
        description: 'Activity log message when supplier returns an error',
      });
    case TicketActivityType.NegotiationSupplierNewVersion:
      return intl.formatMessage({
        id: 'ActivityLogUtils.negotiationSupplierNewVersion',
        defaultMessage: 'New document version received from supplier.',
        description: 'Activity log message when supplier submits a new version',
      });
    case TicketActivityType.NextStage:
      const { newStage, oldStage } = event.data;
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.nextStage',
          defaultMessage:
            '{actor} sent the ticket from {oldStage} to {newStage}.',
          description: 'Activity log message for moving ticket to next stage',
        },
        {
          actor: getEventActor(event),
          oldStage: getPlaceholder([
            'bold',
            getStageText(oldStage.toLowerCase(), intl),
          ]),
          newStage: getPlaceholder([
            'bold',
            getStageText(newStage.toLowerCase(), intl),
          ]),
        },
      );
    case TicketActivityType.DownloadRedline:
      const version = versions.find(
        (version) => version.id === event.data.targetVersionId,
      );
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.downloadRedline',
          defaultMessage: '{actor} downloaded the redlines for {versionLink}.',
          description: 'Activity log message for downloading redlines',
        },
        {
          actor: getEventActor(event),
          versionLink: getPlaceholder([
            'versionLink',
            version ? version.versionNumber : '',
            ticketId,
          ]),
        },
      );
    case TicketActivityType.PreviousStage:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.previousStage',
          defaultMessage: '{actor} sent the ticket back to {stage}.',
          description:
            'Activity log message for sending ticket back to previous stage',
        },
        {
          actor: getEventActor(event),
          stage: getPlaceholder([
            'bold',
            getStageText(event.data.newStage.toLowerCase(), intl),
          ]),
        },
      );
    case TicketActivityType.RemoveParticipant:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.removeParticipant',
          defaultMessage: '{actor} removed {user} from the ticket.',
          description:
            'Activity log message for removing participant from ticket',
        },
        {
          actor: getEventActor(event),
          user: getPlaceholder(['user', event.data.user.id]),
        },
      );
    case TicketActivityType.RenameTicket:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.renameTicket',
          defaultMessage: '{actor} renamed {oldName} to {newName}',
          description: 'Activity log message for renaming ticket',
        },
        {
          actor: getEventActor(event),
          oldName: event.data.oldName,
          newName: event.data.newName,
        },
      );
    case TicketActivityType.ResetApprovals:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.resetApprovals',
          defaultMessage: '{actor} reset all signatures.',
          description: 'Activity log message for resetting all signatures',
        },
        {
          actor: getEventActor(event),
        },
      );
    case TicketActivityType.ShareDocument:
      let message;
      if (event.type === 'ticket') {
        message = intl.formatMessage(
          {
            id: 'ActivityLogUtils.shareDocumentTicket',
            defaultMessage: '{actor} shared {versions} with {recipients}.',
            description:
              'Activity log message for sharing document versions from ticket',
          },
          {
            actor: getEventActor(event),
            versions: getSharedVersionsMessage(
              event.data.attachments,
              ticketId,
            ),
            recipients: getShareRecipientsMessage(event.data.emails),
          },
        );
      } else {
        message = intl.formatMessage(
          {
            id: 'ActivityLogUtils.shareDocument',
            defaultMessage: '{actor} shared {versionLink} with {recipients}.',
            description:
              'Activity log message for sharing specific document version',
          },
          {
            actor: getEventActor(event),
            versionLink: getPlaceholder([
              'versionLink',
              event.data.version.tag,
              ticketId,
            ]),
            recipients: getShareRecipientsMessage(event.data.emails),
          },
        );
      }
      return message;
    case TicketActivityType.SignatureUpload:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.signatureUpload',
          defaultMessage: '{actor} uploaded a document with signature(s)',
          description:
            'Activity log message for uploading document with signatures',
        },
        {
          actor: getEventActor(event),
        },
      );
    case TicketActivityType.SignerReassignment:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.signerReassignment',
          defaultMessage: '{actor} reassigned the signature to {user}',
          description: 'Activity log message for reassigning signature to user',
        },
        {
          actor: getEventActor(event),
          user: getPlaceholder(['user', event.data.newSigner.id]),
        },
      );
    case TicketActivityType.TicketCompletion:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.ticketCompletion',
          defaultMessage: '{actor} completed the ticket workflow.',
          description: 'Activity log message for completing ticket workflow',
        },
        {
          actor: getEventActor(event),
        },
      );
    case TicketActivityType.VersionBumpForEditing:
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.versionBumpForEditing',
          defaultMessage: '{actor} created {versionLink} for editing.',
          description:
            'Activity log message for creating version bump for editing',
        },
        {
          actor: getEventActor(event),
          versionLink: getPlaceholder([
            'versionLink',
            event.data.version.tag,
            ticketId,
          ]),
        },
      );
    case TicketActivityType.TurnTrackEdit:
      const previousStartTime = formatDate(
        event.data.previousStartTime,
        'short_datetime',
      );
      const presentText = intl.formatMessage({
        id: 'ActivityLogUtils.present',
        defaultMessage: 'present',
        description:
          'Text indicating current time/present moment in time tracking',
      });
      const previousEndTime = event.data.previousEndTime
        ? formatDate(event.data.previousEndTime, 'short_datetime')
        : presentText;
      const newStartTime = formatDate(
        event.data.newStartTime,
        'short_datetime',
      );
      const newEndTime = event.data.newEndTime
        ? formatDate(event.data.newEndTime, 'short_datetime')
        : presentText;
      return intl.formatMessage(
        {
          id: 'ActivityLogUtils.turnTrackEdit',
          defaultMessage:
            '{actor} edited the time for {party} {breakLine}From: {breakLine}{indent} {previousStartTime} and {previousEndTime} {breakLine}To: {breakLine}{indent} {newStartTime} and {newEndTime}',
          description: 'Activity log message for editing turn tracking time',
        },
        {
          actor: getEventActor(event),
          party: event.data.party,
          breakLine: getPlaceholder(['breakLine', '']),
          indent: getPlaceholder(['indent', '']),
          previousStartTime: getPlaceholder(['bold', previousStartTime]),
          previousEndTime: getPlaceholder(['bold', previousEndTime]),
          newStartTime: getPlaceholder(['bold', newStartTime]),
          newEndTime: getPlaceholder(['bold', newEndTime]),
        },
      );
    default:
      return null;
  }
}

function getEsigProviderText(esignatureProvider: EsignatureType): string {
  switch (esignatureProvider) {
    case EsignatureType.Docusign:
      return 'DocuSign';
    case EsignatureType.Adobesign:
      return 'AdobeSign';
    default:
      return 'Esignature';
  }
}

function getStatusText(status: TicketStatusType, intl: IntlShape): string {
  switch (status) {
    case TicketStatusType.Approved:
      return intl.formatMessage({
        id: 'ActivityLogUtils.statusApproved',
        defaultMessage: 'Approved',
        description: 'Status text for approved ticket',
      });
    case TicketStatusType.Rejected:
      return intl.formatMessage({
        id: 'ActivityLogUtils.statusRejected',
        defaultMessage: 'Rejected',
        description: 'Status text for rejected ticket',
      });
    case TicketStatusType.Done:
      return intl.formatMessage({
        id: 'ActivityLogUtils.statusCompleted',
        defaultMessage: 'Completed',
        description: 'Status text for completed ticket',
      });
    case TicketStatusType.Pending:
      return intl.formatMessage({
        id: 'ActivityLogUtils.statusPending',
        defaultMessage: 'Pending',
        description: 'Status text for pending ticket',
      });
    default:
      return '';
  }
}

function getStageText(stage: TicketStageType, intl: IntlShape): string {
  switch (stage) {
    case TicketStageType.Edit:
      return intl.formatMessage({
        id: 'ActivityLogUtils.stageEdit',
        defaultMessage: 'Edit',
        description: 'Stage text for edit stage',
      });
    case TicketStageType.Review:
      return intl.formatMessage({
        id: 'ActivityLogUtils.stageReview',
        defaultMessage: 'Review',
        description: 'Stage text for review stage',
      });
    case TicketStageType.Sign:
      return intl.formatMessage({
        id: 'ActivityLogUtils.stageSign',
        defaultMessage: 'Sign',
        description: 'Stage text for sign stage',
      });
    case TicketStageType.Finalize:
      return intl.formatMessage({
        id: 'ActivityLogUtils.stageFinalize',
        defaultMessage: 'Finalize',
        description: 'Stage text for finalize stage',
      });
    default:
      return stage;
  }
}
